<script lang="ts">
    import { onMount } from 'svelte'
    import dayjs from 'dayjs'
    import * as xlsx from 'xlsx'
    import Zinput from '../../_common/z/zinput.svelte'
    export let types: { value: string; label: string; removeOnEmpty: boolean }[]
    export let data: any[]
    export let valid: boolean = false
    export let instructions = 'Selecione um arquivo XLSX, CSV ou VCF (VCard) com os dados a serem importados.'
    export let patherns: { pathern: string; fields: string[] }[] = []

    let file
    let csvTableHeader = []
    let csvTable: any[] = []
    let dataSample: any[] = []

    let selectedDDD = ''
    let needDDD = false

    // o select do Zinput é um painel próprio (não o <select> nativo, cuja lista o sistema desenha
    // branca no tema escuro), então a opção "nenhum" precisa vir escrita nas options
    // "Selecione..." e não "Nenhum": escolher o DDD é obrigatório para concluir a importação
    // (ver evaluateCombosChanges), então a opção vazia é um convite a escolher, não uma escolha.
    const dddOptions = [{ value: '', label: 'Selecione...' }, ...Array.from({ length: 90 }, (_, ddd) => ({ value: String(ddd + 10), label: String(ddd + 10) }))]
    $: typeOptions = [{ value: '', label: 'Selecione...' }, ...(types || []).map((type) => ({ value: type.value, label: type.label }))]
    function evaluateCombosChanges() {
        let tempData = []
        if (csvTable.length === 0) {
            valid = false
            return
        }
        let tempValid = true
        if (needDDD && !selectedDDD) tempValid = false
        for (let line = 0; line < csvTable[0].content.length; line++) {
            let item = {}
            for (let col = 0; col < csvTable.length; col++) {
                const type = csvTable[col].type
                if (!type) {
                    tempValid = false
                    continue
                }
                if (type !== '') {
                    let content: string = csvTable[col].content[line]
                    if (['phone', 'fone', 'celular'].includes(type)) {
                        // se possui mais de um telefone que esteja separado por vírgula
                        content = sanitizePhone(content)
                    } else if (type === 'born') {
                        content = sanitizeBorn(content)
                    } else if (type === 'email' || type === 'e-mail' || type === 'mail') {
                        content = sanitizeEmail(content)
                    } else if (type === 'CEP' || type === 'cep') {
                        content = sanitizeCEP(content)
                    } else if (type === 'state') {
                        content = sanitizeUF(content)
                    } else if (type === 'number') {
                        content = sanitizeNumber(content)
                    } else if (['address', 'district', 'city', 'complement'].includes(type)) {
                        content = sanitizeText(content)
                    } else {
                        //
                    }
                    if (content?.trim()) {
                        item[type] = content.trim()
                    }
                }
            }
            if (Object.keys(item).length > 0) {
                tempData.push(item)
            }
        }
        valid = tempValid
        data = tempData
        console.log('data', data)
        console.log('valid', valid)
    }

    interface ICard {
        fn: string
        tel: string
        email: string
    }

    function sanitizeEmail(email: string) {
        if (!email?.includes('@')) return ''
        email = email.toLowerCase()
        email = email.replace(/ /g, '')
        email = email.replace(/\t/g, '')
        return email
    }

    // CEP fica guardado como texto, e é assim que o mapa de clientes o procura: 8 dígitos, sem máscara.
    // Planilha é a origem mais comum destes arquivos e o Excel trata CEP como número, comendo o zero da
    // frente ("01310-100" vira 1310100) — por isso 7 dígitos são completados com o zero de volta.
    // CEP repetido (00000000, 11111111) é preenchimento de formulário, não endereço.
    function sanitizeCEP(cep: string) {
        let digits = (cep || '').replace(/\D/g, '')
        if (digits.length === 7) digits = '0' + digits
        if (digits.length !== 8) return ''
        if (/^(\d)\1{7}$/.test(digits)) return ''
        return digits
    }

    // Rua, bairro, cidade e complemento: texto livre. A limpeza é tirar o que veio da planilha e não é
    // conteúdo — tabulação, quebra de linha e a fila de espaços que sobra quando a célula foi preenchida
    // com a tecla de espaço.
    function sanitizeText(value: string) {
        return (value || '').replace(/[\t\r\n]+/g, ' ').replace(/\s{2,}/g, ' ').trim()
    }

    // Nome do estado por extenso é comum na coluna de UF. Sem acento na chave porque "Ceará" e "Ceara"
    // aparecem os dois. O que não for reconhecido e não tiver exatamente duas letras fica de fora: uma
    // UF errada é pior que uma UF vazia, porque o código do IBGE da cidade é buscado por ela.
    const UF_POR_NOME = {
        ACRE: 'AC',
        ALAGOAS: 'AL',
        AMAPA: 'AP',
        AMAZONAS: 'AM',
        BAHIA: 'BA',
        CEARA: 'CE',
        'DISTRITO FEDERAL': 'DF',
        'ESPIRITO SANTO': 'ES',
        GOIAS: 'GO',
        MARANHAO: 'MA',
        'MATO GROSSO': 'MT',
        'MATO GROSSO DO SUL': 'MS',
        'MINAS GERAIS': 'MG',
        PARA: 'PA',
        PARAIBA: 'PB',
        PARANA: 'PR',
        PERNAMBUCO: 'PE',
        PIAUI: 'PI',
        'RIO DE JANEIRO': 'RJ',
        'RIO GRANDE DO NORTE': 'RN',
        'RIO GRANDE DO SUL': 'RS',
        RONDONIA: 'RO',
        RORAIMA: 'RR',
        'SANTA CATARINA': 'SC',
        'SAO PAULO': 'SP',
        SERGIPE: 'SE',
        TOCANTINS: 'TO',
    }

    function sanitizeUF(uf: string) {
        const limpo = sanitizeText(uf)
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toUpperCase()
        if (UF_POR_NOME[limpo]) return UF_POR_NOME[limpo]
        const letras = limpo.replace(/[^A-Z]/g, '')
        return letras.length === 2 ? letras : ''
    }

    // O número do endereço é texto (tem "123 A", tem "S/N"), mas não tem espaço sobrando nem máscara.
    // O limite de 10 caracteres é o do próprio cadastro de pessoa, que corta nesse tamanho.
    function sanitizeNumber(value: string) {
        return sanitizeText(value).replace(/\s/g, '').substring(0, 10)
    }

    function sanitizeBorn(born: string) {
        // remove espaços se o born tem apenas o dia/mes/ano (sem as horas)
        born = born.replace(/\t/g, '')
        if (born?.length > 5 && born?.length < 13) born = born.replace(/ /g, '')
        born = born.replace('/-', '/') // file specific
        born = born.replace('T', ' ')
        let stringDate = born.split(' ')[0] // get just date (not time)
        // detect separator
        const slashCount = stringDate.split('/').length
        const hiffenCount = stringDate.split('-').length
        const separator = slashCount > hiffenCount ? '/' : '-'

        if (separator === '/') {
            const elements = stringDate.split('/')
            if (elements.length < 3) return null
            stringDate = elements[2] + '-' + elements[1] + '-' + elements[0]
        }
        // se veio hora, então remove
        const bornDate = new Date(stringDate)
        if (!dayjs(bornDate).isValid()) {
            return ''
        }
        return stringDate // ALERT: dont return bornDate. needs to be string (timezone and other faults)
    }

    function sanitizePhone(phone: string) {
        phone = phone.replace(/\t/g, '') // remove tabs
        phone = phone.split(',')[0]
        phone = phone.split(':')[0]
        phone = phone.trim()
        // previne caso possua mais de 1 telefone na mesma linha (com '(' no ddd, por exemplo) (na forma abaixo está pegando o segundo telefone)
        if (phone.split('(').length > 1) {
            phone = phone.split('(')[1]
            //console.log('phone', phone)
        }
        phone = phone.replace(/[^0-9+]/g, '')
        if (phone.length <= 9 && phone.length >= 8) phone = selectedDDD + phone
        return phone
    }

    function convertXLSToCsv(content) {
        let workBook = xlsx.read(content)
        workBook = convertXLSDatesToString(workBook)
        const result = xlsx.write(workBook, { bookType: 'csv', type: 'string' })
        return result
    }

    // algumas planilhas vêm com as datas armazenadas como número. convertemos este número para evitar problemas de conversão.
    function convertXLSDatesToString(workbook) {
        for (const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName]
            // Percorre todas as células
            for (const cell in sheet) {
                if (cell[0] === '!') continue // ignora metadados
                const value = sheet[cell].v
                const type = sheet[cell].t
                const show = sheet[cell].w
                if (type !== 'n') continue
                if (show.includes(' ')) continue // ignora se possui hora
                if (show.length > 8) continue // ignora se possui hora
                if (show.split('/').length !== 3) continue
                const jsDate = new Date((value - 25569) * 86400 * 1000)
                jsDate.setMinutes(jsDate.getMinutes() + jsDate.getTimezoneOffset())
                sheet[cell].v = dayjs(jsDate).format('YYYY-MM-DD')
                sheet[cell].w = sheet[cell].v
                sheet[cell].t = 's'
            }
        }
        return workbook
    }

    function convertVCardToCsv(content) {
        content = content.replaceAll('item1.', '')
        content = content.replaceAll('item2.', '')
        const blocks = content.split('END:VCARD')
        const cards: ICard[] = []
        const onlyNumbers = /\d+/g
        for (const block of blocks) {
            let lines = block.split('\n')
            let card: ICard = { email: '', fn: '', tel: '' }
            for (const line of lines) {
                const header: string = line.split(':')[0].split(';')[0]
                const content: string = line.split(':')[line.split(':').length - 1]
                // nome
                if (header === 'FN' && !!content) {
                    card.fn = content
                }
                if (header === 'TEL' && !!content) {
                    card.tel = content?.match(onlyNumbers)?.join('')
                }
                if (header === 'EMAIL' && content?.includes('@')) {
                    card.email = content
                }
            }
            if (card.tel || card.email) {
                if (!cards.find((el) => el.email === card.email && el.tel == card.tel)) {
                    cards.push(card)
                }
            }
        }
        let str = 'Nome;Telefone;EMail'
        for (const card of cards) {
            str += '\n' + card.fn + ';' + card.tel + ';' + card.email
        }
        return str
    }

    let firstLine = ''
    function evaluateFilesChanges(content) {
        firstLine = ''
        csvTable = []
        csvTableHeader = []
        dataSample = []
        if (content) {
            let csv: string
            //csv = content.replace(/[^"'],\n/gm, ',')
            csv = content
            // substitui vírgulas dentro de aspas e remove quebras de linha
            csv = csv.replace(/"[^"]*"/g, (z) => z.replace(/[\r\n]|,+/g, ' '))
            csv = csv.replace(/\n$/, '')
            let separador = getFileSeparator(csv)
            // se possui apenas 1 linha retorna
            if (csv.split('\n').length <= 1) {
                console.log('only_one_line')
                return false
            }
            // se possui apenas 1 coluna retorna
            if (csv.split('\n')[0].split(separador).length < 2) {
                console.log('only_one_col')
                return false
            }
            let linhas = csv.split('\n')
            if (linhas.length === 0) {
                console.log('empty_file')
                return false
            }

            // percorre cada coluna
            firstLine = linhas[0]
            const totalCols = linhas[0].split(separador).length
            for (let index = 0; index < totalCols; index++) {
                let csvCol: { type: string; bestColSamplePosition: number; content: string[] } = {
                    type: '',
                    bestColSamplePosition: 0,
                    content: [],
                }
                //percorre cada linha pegando apenas a coluna especificada em 'index'
                let headerChecked = false
                let linePos = 0
                let foundBestColPos = false
                for (let linha of linhas) {
                    if (!linha) continue
                    if (linha.length > 500) {
                        console.log('long_line', linha)
                    }
                    // verifica se a primeira linha é um header (se for header não possuirá uma quantidade rasoável de números)
                    if (!headerChecked) {
                        headerChecked = true
                        const numbers = linha.replace(/[^0-9]/g, '')
                        if (numbers.length < 8) {
                            if (csvTableHeader.length === 0) csvTableHeader = linha.split(separador)
                            continue
                        }
                    }
                    // quebra a linha nas rows e então pega apenas o 'index' específico
                    let rows = linha.split(separador)
                    if (rows.length < totalCols) continue
                    try {
                        csvCol.content.push(rows[index].replace(/"/g, '').replace(/\r/g, '').trim())
                    } catch (error) {
                        console.log(csvCol.content)
                    }
                    if (!foundBestColPos && rows[index].length > 0) {
                        foundBestColPos = true
                        csvCol.bestColSamplePosition = linePos
                    }
                    linePos++
                }
                let isEmpty = csvCol.content.filter((el) => el.length > 0).length <= 1 //1 pois há o header
                if (!isEmpty) {
                    // adiciona a coluna na tabela caso ela possua conteúdo
                    csvTable.push(csvCol)
                } else {
                    // remove o header
                    try {
                        if (csvTableHeader.length > 0) {
                            csvTableHeader.splice(csvTable.length, 1)
                        }
                    } catch (error) {}
                }
            }
        }
        // // remove empties
        // let itemToRemove = []
        // for (let index = csvTable.length - 1; index >= 0; index--) {
        //     const type = types.find((el) => el.value === csvTable[index].type)
        //     if (!type || type.removeOnEmpty === false) continue
        //     let pos = 0
        //     // se é obrigatório possuir valor...
        //     for (const cell of csvTable[index].content) {
        //         // se não tem valor registra a posição para mais abaixo remover de todas as celulas de todas as colunas
        //         if (!cell.type) {
        //             itemToRemove.push(pos)
        //             break
        //         }
        //         pos++
        //     }
        // }
        // for (const item of itemToRemove) {
        //     for (const col of itemToRemove) {
        //         item.content.splice(col, 1)
        //     }
        // }
        // generate sample data
        if (csvTableHeader.length > 0) dataSample.push(csvTableHeader)
        let wasAdded = []
        for (const tableCol of csvTable) {
            let line = []
            let bestColSamplePosition = tableCol.bestColSamplePosition
            // se a melhor coluna de amostra já foi adicionada, então pula para a próxima coluna
            if (wasAdded.includes(bestColSamplePosition)) {
                bestColSamplePosition = Math.floor(Math.random() * (tableCol.content.length - 1))
                if (wasAdded.includes(bestColSamplePosition)) continue
            }
            wasAdded.push(bestColSamplePosition)
            // adiciona a linha escolhida na tabela de amostra
            for (const tableCol2 of csvTable) {
                line.push(tableCol2.content[bestColSamplePosition])
            }
            if (line.length > 0) {
                dataSample.push(line)
            }
        }
        // se os exemplos foram poucos, popula com registros aleatórios...
        if (csvTable.length === 0) {
            window.error('Não foi possível importar o arquivo.\nVerifique se ele possui conteúdo.\nVerifique se o conteúdo é válido.')
            return
        }
        for (let index = Math.floor(csvTable[0].content.length / 2); index < csvTable[0].content.length; index++) {
            let line = []
            for (const tableCol2 of csvTable) {
                line.push(tableCol2.content[index])
            }
            if (!dataSample.some((el) => JSON.stringify(el) == JSON.stringify(line))) dataSample.push(line)
            if (dataSample.length >= 35) break
        }

        evaluatePatherns()

        console.log('csvTable', csvTable)
        console.log('dataSample', dataSample)
    }

    function hygienizePathern(text: string) {
        return text.replace(/\n/g, '').replace(/\r/g, '').replace(/;/g, '').replace(/,/g, '')
    }
    function evaluatePatherns() {
        if (!patherns || patherns.length === 0) return
        console.log('first_line', firstLine)
        const pathern = patherns.find((el) => hygienizePathern(firstLine).startsWith(hygienizePathern(el.pathern)))
        if (!pathern) return
        for (let index = 0; index < csvTable.length; index++) {
            if (pathern.fields.length <= index) continue
            csvTable[index].type = pathern.fields[index]
        }
        csvTable = csvTable
        evaluateCombosChanges()
    }

    function getFileSeparator(content) {
        let lines = content.split('\n')
        let line
        if (lines.length >= 2) {
            line = lines[0]
        } else {
            return '\n'
        }

        for (const chr of line) {
            if (chr === ',' || chr === ';' || chr === '\t') return chr
        }
        return '\n'
    }

    function clear() {
        csvTable = []
        csvTableHeader = []
        dataSample = []
        valid = false
        data = []
        selectedDDD = ''
    }
    function detectFileChange() {
        clear()
        if (!file.files || !file.files[0]) {
            return
        }
        let filePath = file.files[0]
        let reader = new FileReader()
        let isXls = false
        if (filePath.name.includes('.xlsx')) {
            isXls = true
            reader.readAsArrayBuffer(filePath)
        } else {
            reader.readAsText(filePath, 'utf-8')
        }

        let reload = false
        reader.onload = (e) => {
            let content: string = ''
            if (isXls) {
                content = convertXLSToCsv(reader.result)
            } else {
                content = reader.result as string // cont.processaCSV(reader.result)
                if (content != null) {
                    if (reload === false) {
                        let knownChars = ['�', '�', '�', '�', '�', '�', '�', '�', '�', '�']
                        for (const chr of knownChars) {
                            if (content.includes(chr)) {
                                reload = true
                                reader.readAsText(filePath, 'iso-8859-1')
                                return
                            }
                        }
                    }
                }
                if (content.includes('BEGIN:VCARD')) content = convertVCardToCsv(content)
            }
            evaluateFilesChanges(content)
        }
    }

    onMount(() => {
        for (const item of types) {
            if (!item.value) {
                throw new Error("AudienceFileImport: All types need a 'value'")
            }
        }
        needDDD = types.some((el) => ['phone', 'fone', 'celular'].includes(el.value))
    })
</script>

<div class="file-import">
    <div class="row">
        <div class="col-6">
            <div>{instructions}</div>
            <input class="form-control mt-2" type="file" accept=".txt,.csv,.tsv,.vcf,.xlsx" id="formFile" on:change={detectFileChange} bind:this={file} />
        </div>
    </div>
    {#if csvTable.length > 0}
        {#if needDDD}
            <div class="row mt-3 mb-2">
                <div class="col-5 ddd-field">
                    <Zinput label="DDD para os telefones que vierem sem DDD" type="select" options={dddOptions} bind:value={selectedDDD} on:change={evaluateCombosChanges} />
                </div>
            </div>
        {/if}
        <div class="row mt-3 mb-2">
            <div class="col">Classifique cada coluna conforme seu tipo de dados:</div>
        </div>
        <div class="file-content">
            {#each csvTable as col, i}
                <div class="file-col">
                    <Zinput type="select" options={typeOptions} bind:value={col.type} on:change={evaluateCombosChanges} />
                    <div class="file-sample">
                        {#each dataSample as item}
                            <div>{item[i]?.length > 0 ? item[i] : '-'}</div>
                        {/each}
                        {#if col.content.length > dataSample.length}
                            <div>...</div>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    /* As colunas do arquivo quebram em linhas, em vez de andarem numa faixa que rola para o lado.
       A faixa que rola é um contêiner de recorte: ela cortava a lista do select que abre logo
       acima ou abaixo do campo (ver "armadilhas" no CLAUDE.md). Quebrando, nada recorta, e um
       arquivo de dez colunas deixa de exigir rolagem lateral para ser conferido. */
    .file-content {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        font-size: 13px;
    }
    .file-col {
        flex: 1 1 190px;
        min-width: 190px;
        max-width: 280px;
    }
    /* o recorte com reticências é de cada linha de exemplo, nunca da coluna inteira */
    /* opacidade em vez de um cinza fixo: #ccc só funcionava no tema escuro */
    .file-sample {
        margin-top: 6px;
        opacity: 0.75;
    }
    .file-sample > div {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
    .ddd-field {
        max-width: 340px;
    }
</style>
